import {
  beginSignIn,
  clearTokens,
  completeSignInIfNeeded,
  signOut,
  type AdminConfig,
} from './auth';
import {
  createSession,
  deleteSession,
  fetchDraft,
  fetchPublishStatus,
  fetchSession,
  fetchSessionOrNull,
  listSessions,
  publishSession,
  renameSession,
  sendMessage,
  streamRun,
  whoami,
  type PublishStatusView,
  type SessionSummary,
  type SessionView,
} from './api';
import { renderDraftPreview } from './preview';

const ACTIVE_SESSION_KEY = 'comita.admin.activeSessionId';
const PANEL_LAYOUT_KEY = 'comita.admin.panelLayout';

interface PanelLayout {
  railCollapsed: boolean;
  chatCollapsed: boolean;
}

function requireEl<T extends HTMLElement>(id: string, ctor: { new (): T }): T {
  const el = document.getElementById(id);
  if (!(el instanceof ctor)) {
    throw new Error(`Missing #${id}`);
  }
  return el;
}

async function loadConfig(): Promise<AdminConfig> {
  const response = await fetch('/config.json');
  if (!response.ok) {
    throw new Error(`Config unavailable (${response.status}).`);
  }
  return (await response.json()) as AdminConfig;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

/**
 * Match http(s) URLs in plain text. Trailing punctuation like `).,;:` is
 * kept out of the href when present after the URL.
 */
const URL_IN_TEXT =
  /\bhttps?:\/\/[^\s<>"'`]+[^\s<>"'`.,;:!?)\]}]|\bhttps?:\/\/[^\s<>"'`]+/g;

function trimUrlMatch(raw: string): { href: string; trailing: string } {
  let href = raw;
  let trailing = '';
  while (href.length > 0) {
    const last = href.charAt(href.length - 1);
    if (
      last === '.' ||
      last === ',' ||
      last === ';' ||
      last === ':' ||
      last === '!' ||
      last === '?' ||
      last === ')' ||
      last === ']' ||
      last === '}'
    ) {
      trailing = `${last}${trailing}`;
      href = href.slice(0, -1);
      continue;
    }
    break;
  }
  return { href, trailing };
}

function isSafeHttpUrl(href: string): boolean {
  try {
    const parsed = new URL(href);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Fill a container with plain text, turning http(s) URLs into new-tab links. */
function appendLinkedText(container: HTMLElement, text: string): void {
  URL_IN_TEXT.lastIndex = 0;
  let lastIndex = 0;
  let match: RegExpExecArray | null = URL_IN_TEXT.exec(text);
  while (match !== null) {
    const start = match.index;
    if (start > lastIndex) {
      container.append(document.createTextNode(text.slice(lastIndex, start)));
    }
    const { href, trailing } = trimUrlMatch(match[0] ?? '');
    if (href.length > 0 && isSafeHttpUrl(href)) {
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.className = 'bubble__link';
      anchor.textContent = href;
      container.append(anchor);
    } else {
      container.append(document.createTextNode(match[0] ?? ''));
    }
    if (trailing.length > 0) {
      container.append(document.createTextNode(trailing));
    }
    lastIndex = start + (match[0]?.length ?? 0);
    match = URL_IN_TEXT.exec(text);
  }
  if (lastIndex < text.length) {
    container.append(document.createTextNode(text.slice(lastIndex)));
  }
}

function appendBubble(
  log: HTMLElement,
  text: string,
  kind: 'user' | 'assistant' | 'error' | 'status',
  options?: { durationMs?: number },
): void {
  const bubble = document.createElement('div');
  bubble.className = `bubble bubble--${kind}`;

  const body = document.createElement('div');
  body.className = 'bubble__body';
  appendLinkedText(body, text);
  bubble.append(body);

  if (
    options !== undefined &&
    typeof options.durationMs === 'number' &&
    Number.isFinite(options.durationMs)
  ) {
    const pill = document.createElement('span');
    pill.className = 'bubble__pill';
    pill.textContent = formatDuration(options.durationMs);
    pill.title = 'Time from send to draft preview';
    pill.setAttribute(
      'aria-label',
      `Run took ${formatDuration(options.durationMs)}`,
    );
    bubble.append(pill);
  }

  log.append(bubble);
  log.scrollTop = log.scrollHeight;
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;
const PUBLISH_POLL_INTERVAL_MS = 4000;
const PUBLISH_POLL_TIMEOUT_MS = 20 * 60 * 1000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function publishPhaseLabel(status: PublishStatusView): string {
  const phase = status.currentPhase;
  if (phase !== null && phase.length > 0) {
    const friendly = phase
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
    return `publishing · ${friendly}`;
  }
  if (status.buildStatus !== null && status.buildStatus.length > 0) {
    return `publishing · ${status.buildStatus.toLowerCase()}`;
  }
  return 'publishing';
}

/**
 * Polls CodeBuild via GET /publish until the site rebuild finishes (or times out).
 */
async function waitForPublish(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
  onTick: (status: PublishStatusView) => void,
): Promise<PublishStatusView> {
  const deadline = Date.now() + PUBLISH_POLL_TIMEOUT_MS;
  let latest = await fetchPublishStatus(config, idToken, sessionId);
  onTick(latest);
  while (!latest.complete && Date.now() < deadline) {
    await delay(PUBLISH_POLL_INTERVAL_MS);
    latest = await fetchPublishStatus(config, idToken, sessionId);
    onTick(latest);
  }
  if (!latest.complete) {
    throw new Error(
      'Site rebuild is still running — reload this session to check again.',
    );
  }
  return latest;
}

function readStoredSessionId(): string | null {
  try {
    const value = window.localStorage.getItem(ACTIVE_SESSION_KEY);
    if (value === null || value.trim().length === 0) {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

function storeSessionId(sessionId: string): void {
  try {
    window.localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
  } catch {
    // ignore quota / private mode
  }
}

function clearStoredSessionId(): void {
  try {
    window.localStorage.removeItem(ACTIVE_SESSION_KEY);
  } catch {
    // ignore
  }
}

function readPanelLayout(): PanelLayout {
  try {
    const raw = window.localStorage.getItem(PANEL_LAYOUT_KEY);
    if (raw === null || raw.length === 0) {
      return { railCollapsed: false, chatCollapsed: false };
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      return { railCollapsed: false, chatCollapsed: false };
    }
    const record = parsed as {
      railCollapsed?: unknown;
      chatCollapsed?: unknown;
    };
    return {
      railCollapsed: record.railCollapsed === true,
      chatCollapsed: record.chatCollapsed === true,
    };
  } catch {
    return { railCollapsed: false, chatCollapsed: false };
  }
}

function storePanelLayout(layout: PanelLayout): void {
  try {
    window.localStorage.setItem(PANEL_LAYOUT_KEY, JSON.stringify(layout));
  } catch {
    // ignore quota / private mode
  }
}

function formatWhen(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso.length === 0) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function summaryLine(session: SessionSummary | SessionView): string {
  if (
    session.lastAssistantText !== null &&
    session.lastAssistantText.length > 0
  ) {
    return session.lastAssistantText;
  }
  if (session.status === 'published') {
    return 'Published to build-env/dev';
  }
  return 'No edits yet';
}

/** Prefer the human label; fall back to the stable session id. */
function sessionTitle(session: {
  sessionId: string;
  label?: string | null;
}): string {
  const label = session.label ?? null;
  if (label !== null && label.length > 0) {
    return label;
  }
  return session.sessionId;
}

/**
 * Ask for a session name. Returns undefined if the dialog is cancelled,
 * null if left blank (clear / unnamed), otherwise the trimmed string.
 */
function promptSessionLabel(
  current: string | null,
  options?: { creating?: boolean },
): string | null | undefined {
  const creating = options?.creating === true;
  const message = creating
    ? 'Name this session (optional), e.g. "Test color change":'
    : 'Session name (leave blank to clear):';
  const initial = current ?? '';
  const answer = window.prompt(message, initial);
  if (answer === null) {
    return undefined;
  }
  const trimmed = answer.trim().replace(/\s+/g, ' ');
  if (trimmed.length === 0) {
    return null;
  }
  return trimmed.slice(0, 80);
}

/**
 * Poll fallback when the SSE Function URL is unavailable. Session reads
 * reconcile run status server-side.
 */
async function waitForSessionPoll(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
  onTick: (session: SessionView) => void,
): Promise<SessionView> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  let latest = await fetchSession(config, idToken, sessionId);
  while (latest.status === 'running' && Date.now() < deadline) {
    onTick(latest);
    await delay(POLL_INTERVAL_MS);
    latest = await fetchSession(config, idToken, sessionId);
  }
  if (latest.status === 'running') {
    throw new Error('Agent is still working — reload to check again shortly.');
  }
  return latest;
}

function isSessionPayload(data: unknown): data is { session: SessionView } {
  if (data === null || typeof data !== 'object') {
    return false;
  }
  const session = (data as { session?: unknown }).session;
  return session !== null && typeof session === 'object';
}

function toolStatusLabel(data: unknown): string | null {
  if (data === null || typeof data !== 'object') {
    return null;
  }
  const record = data as {
    name?: unknown;
    status?: unknown;
  };
  if (typeof record.name !== 'string') {
    return null;
  }
  const status = typeof record.status === 'string' ? record.status : 'running';
  return `${record.name} — ${status}`;
}

/**
 * Prefer Cursor SSE via the streaming Function URL; fall back to poll
 * reconciliation if streaming fails (missing config, CORS, network).
 */
async function waitForRun(
  config: AdminConfig,
  idToken: string,
  sessionId: string,
  runId: string | null,
  onStatus: (label: string) => void,
): Promise<SessionView> {
  if (runId === null || runId.length === 0) {
    return waitForSessionPoll(config, idToken, sessionId, (session) => {
      onStatus(session.status);
    });
  }

  const elapsedFrom = Date.now();
  try {
    let latest: SessionView | null = null;
    for await (const evt of streamRun(config, idToken, sessionId, runId)) {
      const seconds = Math.round((Date.now() - elapsedFrom) / 1000);
      if (
        evt.event === 'status' &&
        typeof evt.data === 'object' &&
        evt.data !== null
      ) {
        const status = (evt.data as { status?: string }).status;
        onStatus(
          typeof status === 'string'
            ? `${status} ${seconds}s`
            : `running ${seconds}s`,
        );
      } else if (evt.event === 'tool_call') {
        const label = toolStatusLabel(evt.data);
        if (label !== null) {
          onStatus(`${label} (${seconds}s)`);
        }
      } else if (
        evt.event === 'session_ready' ||
        evt.event === 'session_error'
      ) {
        if (isSessionPayload(evt.data)) {
          latest = evt.data.session;
        }
      } else if (
        evt.event === 'error' &&
        typeof evt.data === 'object' &&
        evt.data !== null
      ) {
        const message = (evt.data as { message?: string }).message;
        if (typeof message === 'string' && message.length > 0) {
          onStatus(message);
        }
      }
    }
    if (latest !== null) {
      return latest;
    }
    return fetchSession(config, idToken, sessionId);
  } catch (error) {
    console.warn('SSE stream unavailable; falling back to poll', error);
    onStatus('streaming unavailable — polling');
    return waitForSessionPoll(config, idToken, sessionId, () => {
      const seconds = Math.round((Date.now() - elapsedFrom) / 1000);
      onStatus(`running ${seconds}s`);
    });
  }
}

async function refreshPreview(
  config: AdminConfig,
  idToken: string,
  session: SessionView,
  previewRoot: HTMLElement,
  previewUpdated: HTMLElement,
): Promise<void> {
  const draft = await fetchDraft(config, idToken, session.sessionId);
  renderDraftPreview(previewRoot, draft.home, draft.site, draft.theme, {
    siteOrigin: config.siteOrigin,
  });
  previewUpdated.textContent =
    draft.updatedAt !== null ? `Updated ${draft.updatedAt}` : '';
}

async function init(): Promise<void> {
  const gate = requireEl('gate', HTMLElement);
  const gateLead = requireEl('gate-lead', HTMLElement);
  const workspace = requireEl('workspace', HTMLElement);
  const signIn = requireEl('sign-in', HTMLButtonElement);
  const signOutBtn = requireEl('sign-out', HTMLButtonElement);
  const userEmail = requireEl('user-email', HTMLElement);
  const chatLog = requireEl('chat-log', HTMLElement);
  const chatForm = requireEl('chat-form', HTMLFormElement);
  const chatInput = requireEl('chat-input', HTMLTextAreaElement);
  const chatSend = requireEl('chat-send', HTMLButtonElement);
  const publishBtn = requireEl('publish-dev', HTMLButtonElement);
  const newSessionBtn = requireEl('new-session', HTMLButtonElement);
  const renameSessionBtn = requireEl('rename-session', HTMLButtonElement);
  const deleteSessionBtn = requireEl('delete-session', HTMLButtonElement);
  const sessionList = requireEl('session-list', HTMLElement);
  const sessionLabelEl = requireEl('session-label', HTMLElement);
  const sessionBranch = requireEl('session-branch', HTMLElement);
  const sessionStatus = requireEl('session-status', HTMLElement);
  const previewRoot = requireEl('preview-root', HTMLElement);
  const previewUpdated = requireEl('preview-updated', HTMLElement);
  const collapseRailBtn = requireEl('collapse-rail', HTMLButtonElement);
  const collapseChatBtn = requireEl('collapse-chat', HTMLButtonElement);
  const expandRailBtn = requireEl('expand-rail', HTMLButtonElement);
  const expandChatBtn = requireEl('expand-chat', HTMLButtonElement);
  const panelReopen = requireEl('panel-reopen', HTMLElement);

  let config: AdminConfig;
  try {
    config = await loadConfig();
  } catch (error) {
    gateLead.textContent =
      error instanceof Error ? error.message : 'Config missing.';
    return;
  }

  signIn.addEventListener('click', () => {
    void beginSignIn(config);
  });
  signOutBtn.addEventListener('click', () => {
    clearStoredSessionId();
    signOut(config);
  });

  let tokens;
  try {
    tokens = await completeSignInIfNeeded(config);
  } catch (error) {
    clearTokens();
    gateLead.textContent =
      error instanceof Error ? error.message : 'Sign-in failed.';
    return;
  }

  if (tokens === null) {
    return;
  }

  let me;
  try {
    me = await whoami(config, tokens.idToken);
  } catch (error) {
    clearTokens();
    gateLead.textContent =
      error instanceof Error ? error.message : 'Not authorized.';
    return;
  }

  gate.hidden = true;
  workspace.hidden = false;
  signIn.hidden = true;
  signOutBtn.hidden = false;
  userEmail.textContent = me.email;

  let panelLayout = readPanelLayout();
  const applyPanelLayout = (): void => {
    workspace.classList.toggle(
      'workspace--rail-collapsed',
      panelLayout.railCollapsed,
    );
    workspace.classList.toggle(
      'workspace--chat-collapsed',
      panelLayout.chatCollapsed,
    );
    collapseRailBtn.setAttribute(
      'aria-expanded',
      panelLayout.railCollapsed ? 'false' : 'true',
    );
    collapseChatBtn.setAttribute(
      'aria-expanded',
      panelLayout.chatCollapsed ? 'false' : 'true',
    );
    const anyCollapsed =
      panelLayout.railCollapsed === true || panelLayout.chatCollapsed === true;
    panelReopen.hidden = !anyCollapsed;
    storePanelLayout(panelLayout);
  };
  const setRailCollapsed = (collapsed: boolean): void => {
    panelLayout = { ...panelLayout, railCollapsed: collapsed };
    applyPanelLayout();
  };
  const setChatCollapsed = (collapsed: boolean): void => {
    panelLayout = { ...panelLayout, chatCollapsed: collapsed };
    applyPanelLayout();
  };
  applyPanelLayout();
  collapseRailBtn.addEventListener('click', () => {
    setRailCollapsed(true);
  });
  collapseChatBtn.addEventListener('click', () => {
    setChatCollapsed(true);
  });
  expandRailBtn.addEventListener('click', () => {
    setRailCollapsed(false);
  });
  expandChatBtn.addEventListener('click', () => {
    setChatCollapsed(false);
  });

  let session: SessionView;
  let history: SessionSummary[] = [];
  let busy = false;
  /**
   * Coarse lifecycle state of the active session (`running`, `ready`, …).
   * The chat header may show a more verbose label (tool name, elapsed
   * seconds); the rail always shows this so both stay in sync mid-run.
   */
  let liveStatus = '';

  const setBusy = (next: boolean): void => {
    busy = next;
    chatSend.disabled = next;
    publishBtn.disabled = next;
    newSessionBtn.disabled = next;
    renameSessionBtn.disabled = next;
    deleteSessionBtn.disabled = next;
  };

  const applySessionChrome = (next: SessionView): void => {
    const title = sessionTitle(next);
    const hasLabel =
      next.label !== null && next.label !== undefined && next.label.length > 0;
    sessionLabelEl.textContent = hasLabel ? title : '';
    sessionLabelEl.hidden = !hasLabel;
    sessionBranch.textContent = next.branch;
    sessionBranch.title = hasLabel
      ? `${title} · ${next.sessionId}`
      : next.sessionId;
    renameSessionBtn.textContent = hasLabel ? 'Rename' : 'Name';
  };

  const renderHistory = (): void => {
    sessionList.replaceChildren();
    for (const item of history) {
      const isActive = item.sessionId === session.sessionId;
      const activeLabel =
        isActive && session.label !== null && session.label !== undefined
          ? session.label
          : item.label;
      const displayItem = { ...item, label: activeLabel };
      const status =
        isActive && liveStatus.length > 0 ? liveStatus : item.status;
      const hasLabel =
        displayItem.label !== null &&
        displayItem.label !== undefined &&
        displayItem.label.length > 0;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'session-item';
      button.setAttribute('role', 'listitem');
      if (isActive) {
        button.classList.add('session-item--active');
      }
      const title = document.createElement('span');
      title.className = hasLabel
        ? 'session-item__title'
        : 'session-item__title session-item__title--id';
      title.textContent = sessionTitle(displayItem);
      button.append(title);
      if (hasLabel) {
        const id = document.createElement('span');
        id.className = 'session-item__id';
        id.textContent = item.sessionId;
        button.append(id);
      }
      const meta = document.createElement('span');
      meta.className = 'session-item__meta';
      const when =
        formatWhen(item.draftUpdatedAt) || formatWhen(item.createdAt);
      if (status === 'running' || status === 'publishing') {
        const dot = document.createElement('span');
        dot.className = 'session-item__dot';
        dot.setAttribute('aria-hidden', 'true');
        meta.append(dot);
      }
      meta.append(
        document.createTextNode(
          `${status}${when.length > 0 ? ` · ${when}` : ''}`,
        ),
      );
      const summary = document.createElement('span');
      summary.className = 'session-item__summary';
      summary.textContent = summaryLine(item);
      button.append(meta, summary);
      button.addEventListener('click', () => {
        if (busy || isActive) {
          return;
        }
        void openSession(item.sessionId);
      });
      sessionList.append(button);
    }
  };

  /**
   * Single writer for session status. `detail` is chat-header-only chrome;
   * `coarse` drives the sessions rail so the selected row never lags behind.
   */
  const setSessionStatus = (coarse: string, detail?: string): void => {
    liveStatus = coarse;
    sessionStatus.textContent =
      detail !== undefined && detail.length > 0 ? detail : coarse;
    renderHistory();
  };

  const refreshHistory = async (): Promise<void> => {
    try {
      history = [...(await listSessions(config, tokens.idToken))];
      renderHistory();
    } catch (error) {
      console.warn('Could not load session history', error);
    }
  };

  const showSession = async (
    next: SessionView,
    options?: { resumed?: boolean },
  ): Promise<void> => {
    session = next;
    storeSessionId(session.sessionId);
    applySessionChrome(session);
    setSessionStatus(session.status);
    chatLog.replaceChildren();
    const transcript = session.messages ?? [];
    if (transcript.length > 0) {
      for (const entry of transcript) {
        appendBubble(
          chatLog,
          entry.text,
          entry.role,
          entry.durationMs === null
            ? undefined
            : { durationMs: entry.durationMs },
        );
      }
      if (options?.resumed === true) {
        appendBubble(
          chatLog,
          `Resumed ${sessionTitle(session)}. Continue editing or publish to dev.`,
          'status',
        );
      }
    } else {
      // Sessions created before transcripts were persisted only carry the
      // last agent reply, so reconstruct an approximate log from the record.
      appendBubble(
        chatLog,
        options?.resumed === true
          ? `Resumed ${sessionTitle(session)}. Continue editing or publish to dev.`
          : 'Session ready. Describe a copy change for the homepage.',
        'assistant',
      );
      if (
        session.lastAssistantText !== null &&
        session.lastAssistantText.length > 0
      ) {
        appendBubble(chatLog, session.lastAssistantText, 'assistant');
      }
      if (
        session.lastError !== null &&
        session.lastError.length > 0 &&
        session.status !== 'publishing'
      ) {
        appendBubble(chatLog, session.lastError, 'error');
      }
      if (session.publishedAt !== null && session.status === 'published') {
        appendBubble(
          chatLog,
          `Published ${formatWhen(session.publishedAt)}.`,
          'status',
        );
      }
    }
    if (session.status === 'publishing') {
      appendBubble(
        chatLog,
        'Publish in progress — rebuilding the live site. Status updates will appear here.',
        'status',
      );
    }
    await refreshPreview(
      config,
      tokens.idToken,
      session,
      previewRoot,
      previewUpdated,
    );
    renderHistory();

    if (session.status === 'publishing' && session.publishBuildId !== null) {
      setBusy(true);
      setSessionStatus('publishing');
      void (async () => {
        try {
          const finished = await waitForPublish(
            config,
            tokens.idToken,
            session.sessionId,
            (status) => {
              session = status.session;
              setSessionStatus('publishing', publishPhaseLabel(status));
            },
          );
          session = finished.session;
          setSessionStatus(session.status);
          if (finished.buildStatus === 'SUCCEEDED') {
            appendBubble(
              chatLog,
              `Site rebuild finished. Live at ${finished.siteUrl}`,
              'assistant',
            );
          } else {
            appendBubble(
              chatLog,
              finished.errorMessage ??
                `Site rebuild ${finished.buildStatus ?? 'failed'}.`,
              'error',
            );
          }
          await refreshHistory();
        } catch (error) {
          setSessionStatus('error');
          appendBubble(
            chatLog,
            error instanceof Error
              ? error.message
              : 'Could not follow site rebuild.',
            'error',
          );
        } finally {
          setBusy(false);
        }
      })();
    } else if (session.status === 'running') {
      // Reattach to a run that was still in flight when the page reloaded.
      setBusy(true);
      setSessionStatus('running');
      void (async () => {
        try {
          const finished = await waitForSessionPoll(
            config,
            tokens.idToken,
            session.sessionId,
            () => {
              setSessionStatus('running');
            },
          );
          session = finished;
          setSessionStatus(session.status);
          const last = (finished.messages ?? []).slice(-1)[0];
          if (last !== undefined && last.role !== 'user') {
            appendBubble(
              chatLog,
              last.text,
              last.role,
              last.durationMs === null
                ? undefined
                : { durationMs: last.durationMs },
            );
          }
          await refreshPreview(
            config,
            tokens.idToken,
            session,
            previewRoot,
            previewUpdated,
          );
          await refreshHistory();
        } catch (error) {
          appendBubble(
            chatLog,
            error instanceof Error
              ? error.message
              : 'Could not follow the agent run.',
            'error',
          );
        } finally {
          setBusy(false);
        }
      })();
    }
  };

  const openSession = async (sessionId: string): Promise<void> => {
    setBusy(true);
    try {
      const loaded = await fetchSession(config, tokens.idToken, sessionId);
      await showSession(loaded, { resumed: true });
      await refreshHistory();
    } catch (error) {
      appendBubble(
        chatLog,
        error instanceof Error ? error.message : 'Could not open session.',
        'error',
      );
    } finally {
      setBusy(false);
    }
  };

  const startFreshSession = async (): Promise<void> => {
    const label = promptSessionLabel(null, { creating: true });
    if (label === undefined) {
      return;
    }
    setBusy(true);
    try {
      const created = await createSession(config, tokens.idToken, label);
      await showSession(created);
      await refreshHistory();
    } catch (error) {
      appendBubble(
        chatLog,
        error instanceof Error ? error.message : 'Could not create session.',
        'error',
      );
    } finally {
      setBusy(false);
    }
  };

  const renameActiveSession = async (): Promise<void> => {
    if (busy) {
      return;
    }
    const nextLabel = promptSessionLabel(session.label ?? null);
    if (nextLabel === undefined) {
      return;
    }
    setBusy(true);
    try {
      session = await renameSession(
        config,
        tokens.idToken,
        session.sessionId,
        nextLabel,
      );
      applySessionChrome(session);
      history = history.map((item) =>
        item.sessionId === session.sessionId
          ? { ...item, label: session.label }
          : item,
      );
      renderHistory();
    } catch (error) {
      appendBubble(
        chatLog,
        error instanceof Error ? error.message : 'Could not rename session.',
        'error',
      );
    } finally {
      setBusy(false);
    }
  };

  const deleteActiveSession = async (): Promise<void> => {
    if (busy) {
      return;
    }
    const title = sessionTitle(session);
    const confirmed = window.confirm(
      `Permanently delete "${title}"?\n\nThis removes its saved draft, preview branch, and durable history tags. Published changes are not reverted.`,
    );
    if (!confirmed) {
      return;
    }

    setBusy(true);
    try {
      const deletedId = session.sessionId;
      await deleteSession(config, tokens.idToken, deletedId);
      clearStoredSessionId();
      history = [...(await listSessions(config, tokens.idToken))];

      const next = history[0];
      if (next !== undefined) {
        const loaded = await fetchSession(
          config,
          tokens.idToken,
          next.sessionId,
        );
        await showSession(loaded, { resumed: true });
      } else {
        const created = await createSession(config, tokens.idToken);
        await showSession(created);
        await refreshHistory();
      }
    } catch (error) {
      appendBubble(
        chatLog,
        error instanceof Error ? error.message : 'Could not delete session.',
        'error',
      );
    } finally {
      setBusy(false);
    }
  };

  try {
    const storedId = readStoredSessionId();
    if (storedId !== null) {
      const resumed = await fetchSessionOrNull(
        config,
        tokens.idToken,
        storedId,
      );
      if (resumed !== null) {
        await showSession(resumed, { resumed: true });
      } else {
        clearStoredSessionId();
        const created = await createSession(config, tokens.idToken);
        await showSession(created);
      }
    } else {
      const created = await createSession(config, tokens.idToken);
      await showSession(created);
    }
  } catch (error) {
    appendBubble(
      chatLog,
      error instanceof Error ? error.message : 'Could not create session.',
      'error',
    );
    return;
  }

  await refreshHistory();

  newSessionBtn.addEventListener('click', () => {
    if (busy) {
      return;
    }
    void startFreshSession();
  });

  renameSessionBtn.addEventListener('click', () => {
    void renameActiveSession();
  });

  deleteSessionBtn.addEventListener('click', () => {
    void deleteActiveSession();
  });

  publishBtn.addEventListener('click', () => {
    if (busy) {
      return;
    }
    const confirmed = window.confirm(
      `Publish "${sessionTitle(session)}" to build-env/dev and rebuild https://dev.comitahealth.com?`,
    );
    if (!confirmed) {
      return;
    }
    setBusy(true);
    setSessionStatus('publishing');
    const publishStartedAt = Date.now();
    void (async () => {
      try {
        const result = await publishSession(
          config,
          tokens.idToken,
          session.sessionId,
        );
        session = result.session;
        setSessionStatus(session.status);
        if (result.buildId === null) {
          appendBubble(
            chatLog,
            `Published to build-env/dev. Site rebuild was not started (no CodeBuild project). Open ${result.siteUrl}.`,
            'assistant',
          );
          await refreshHistory();
          return;
        }
        appendBubble(
          chatLog,
          `Merged into build-env/dev. Rebuilding ${result.siteUrl}…`,
          'status',
        );
        const finished = await waitForPublish(
          config,
          tokens.idToken,
          session.sessionId,
          (status) => {
            session = status.session;
            setSessionStatus('publishing', publishPhaseLabel(status));
          },
        );
        session = finished.session;
        setSessionStatus(session.status);
        if (finished.buildStatus === 'SUCCEEDED') {
          appendBubble(
            chatLog,
            `Site rebuild finished. Live at ${finished.siteUrl}`,
            'assistant',
            { durationMs: Date.now() - publishStartedAt },
          );
        } else {
          appendBubble(
            chatLog,
            finished.errorMessage ??
              `Site rebuild ${finished.buildStatus ?? 'failed'}.`,
            'error',
          );
        }
        await refreshHistory();
      } catch (error) {
        setSessionStatus('error');
        appendBubble(
          chatLog,
          error instanceof Error ? error.message : 'Publish failed.',
          'error',
        );
      } finally {
        setBusy(false);
      }
    })();
  });

  chatForm.addEventListener('submit', (event) => {
    event.preventDefault();
    if (busy) {
      return;
    }
    const message = chatInput.value.trim();
    if (message.length === 0) {
      return;
    }
    chatInput.value = '';
    appendBubble(chatLog, message, 'user');
    setBusy(true);
    setSessionStatus('running');
    const requestStartedAt = Date.now();
    void (async () => {
      try {
        const started = await sendMessage(
          config,
          tokens.idToken,
          session.sessionId,
          message,
        );
        session = started.session;
        setSessionStatus(session.status);
        const runId = started.runId ?? session.activeRunId ?? null;
        session = await waitForRun(
          config,
          tokens.idToken,
          session.sessionId,
          runId,
          (label) => {
            setSessionStatus('running', label);
          },
        );
        setSessionStatus(session.status);
        await refreshPreview(
          config,
          tokens.idToken,
          session,
          previewRoot,
          previewUpdated,
        );
        const durationMs = Date.now() - requestStartedAt;
        if (session.lastAssistantText !== null) {
          appendBubble(chatLog, session.lastAssistantText, 'assistant', {
            durationMs,
          });
        } else if (session.lastError !== null) {
          appendBubble(chatLog, session.lastError, 'error', { durationMs });
        } else {
          appendBubble(chatLog, 'Done. Preview updated.', 'assistant', {
            durationMs,
          });
        }
        await refreshHistory();
      } catch (error) {
        setSessionStatus('error');
        appendBubble(
          chatLog,
          error instanceof Error ? error.message : 'Request failed.',
          'error',
        );
      } finally {
        setBusy(false);
      }
    })();
  });
}

void init();
